/**
 * NavigatorX Audit & Security Dashboard
 * 
 * Enterprise-grade audit logging and security monitoring:
 * - Admin action audit logs with filtering
 * - User activity tracking
 * - Security alerts and anomaly detection
 * - GDPR compliance tools
 * - Data export and deletion requests
 * 
 * @author Claude AI for TGE.LA
 * @version 1.0.0
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import {
  Shield,
  FileText,
  AlertTriangle,
  Lock,
  Download,
  Trash2,
  Search,
  RefreshCw,
  Loader2,
  User,
  Clock,
  Eye,
  Filter,
  ChevronDown,
  ChevronRight,
  CheckCircle,
  XCircle,
  Activity,
  Globe,
  Smartphone,
  Key,
  UserX,
  Database
} from 'lucide-react';
import { toast } from 'sonner';
import { Timestamp } from 'firebase/firestore';
import { auth } from '../../../utils/firebase/client';
import {
  getAdminAuditLogs,
  getUserActivityLogs,
  getSecurityAlerts,
  acknowledgeAlert,
  getDataExportRequests,
  getDeletionRequests,
  processDataExport,
  processDeletionRequest,
  getLoginAttempts,
  getApiAccessLogs,
  exportAuditLogs,
  type AdminAuditLog,
  type UserActivityLog,
  type SecurityAlert,
  type DataExportRequest,
  type DeletionRequest,
  type LoginAttempt,
  type ApiAccessLog
} from '../../../utils/services/navigatorXAuditService';

interface AuditSecurityDashboardProps {
  embedded?: boolean;
}

type ActiveTab = 'audit' | 'activity' | 'alerts' | 'compliance' | 'logins';

export default function AuditSecurityDashboard({ embedded = false }: AuditSecurityDashboardProps) {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<ActiveTab>('audit');
  
  // Data
  const [auditLogs, setAuditLogs] = useState<AdminAuditLog[]>([]);
  const [activityLogs, setActivityLogs] = useState<UserActivityLog[]>([]);
  const [alerts, setAlerts] = useState<SecurityAlert[]>([]);
  const [exportRequests, setExportRequests] = useState<DataExportRequest[]>([]);
  const [deletionRequests, setDeletionRequests] = useState<DeletionRequest[]>([]);
  const [loginAttempts, setLoginAttempts] = useState<LoginAttempt[]>([]);
  
  // Filters
  const [actionFilter, setActionFilter] = useState('');
  const [userIdFilter, setUserIdFilter] = useState('');
  const [dateRange, setDateRange] = useState<'today' | 'week' | 'month' | 'all'>('week');
  const [expandedLog, setExpandedLog] = useState<string | null>(null);
  
  const adminId = auth.currentUser?.uid || 'unknown';

  useEffect(() => {
    loadAllData();
  }, [dateRange]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      const startDate = getStartDate(dateRange);
      
      const [audit, activity, alertsData, exports, deletions, logins] = await Promise.all([
        getAdminAuditLogs({ limitCount: 200, startDate }),
        getUserActivityLogs({ limitCount: 200, startDate }),
        getSecurityAlerts(),
        getDataExportRequests(),
        getDeletionRequests(),
        getLoginAttempts({ limitCount: 100 })
      ]);
      
      setAuditLogs(audit);
      setActivityLogs(activity);
      setAlerts(alertsData);
      setExportRequests(exports);
      setDeletionRequests(deletions);
      setLoginAttempts(logins);
    } catch (error) {
      console.error('Error loading audit data:', error);
      toast.error('Failed to load audit data');
    } finally {
      setLoading(false);
    }
  };

  const getStartDate = (range: string): Date | undefined => {
    const now = new Date();
    switch (range) {
      case 'today':
        now.setHours(0, 0, 0, 0);
        return now;
      case 'week':
        now.setDate(now.getDate() - 7);
        return now;
      case 'month':
        now.setDate(now.getDate() - 30);
        return now;
      default:
        return undefined;
    }
  };

  const handleAcknowledgeAlert = async (alertId: string) => {
    try {
      await acknowledgeAlert(alertId, adminId);
      toast.success('Alert acknowledged');
      loadAllData();
    } catch (error) {
      toast.error('Failed to acknowledge alert');
    }
  };

  const handleProcessExport = async (requestId: string) => {
    try {
      await processDataExport(requestId, adminId);
      toast.success('Export processed');
      loadAllData();
    } catch (error) {
      toast.error('Failed to process export');
    }
  };

  const handleProcessDeletion = async (requestId: string, approve: boolean) => {
    try {
      await processDeletionRequest(requestId, approve, adminId);
      toast.success(approve ? 'Deletion approved' : 'Deletion rejected');
      loadAllData();
    } catch (error) {
      toast.error('Failed to process deletion');
    }
  };

  const handleExportLogs = async () => {
    try {
      const csv = await exportAuditLogs(auditLogs);
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit_logs_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Logs exported');
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const formatDate = (ts: any) => {
    if (!ts) return 'N/A';
    const d = ts instanceof Timestamp ? ts.toDate() : new Date(ts);
    return d.toLocaleString();
  };

  const getActionColor = (action: string) => {
    if (action.includes('DELETE') || action.includes('REMOVE') || action.includes('BLOCK')) {
      return 'text-red-600 bg-red-50';
    }
    if (action.includes('CREATE') || action.includes('ADD') || action.includes('GRANT')) {
      return 'text-green-600 bg-green-50';
    }
    if (action.includes('UPDATE') || action.includes('EDIT') || action.includes('CHANGE')) {
      return 'text-blue-600 bg-blue-50';
    }
    return 'text-gray-600 bg-gray-50';
  };

  const getAlertSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-600 text-white';
      case 'high': return 'bg-orange-500 text-white';
      case 'medium': return 'bg-yellow-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  // Filter logs
  const filteredAuditLogs = auditLogs.filter(log => {
    if (actionFilter && !log.action.toLowerCase().includes(actionFilter.toLowerCase())) return false;
    if (userIdFilter && !log.adminId.includes(userIdFilter)) return false;
    return true;
  });

  const unacknowledgedAlerts = alerts.filter(a => !a.acknowledged);

  if (loading) {
    return (
      <div className={`flex items-center justify-center ${embedded ? 'py-12' : 'min-h-[400px]'}`}>
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className={embedded ? '' : 'p-6'}>
      {!embedded && (
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Audit & Security</h2>
            <p className="text-gray-500">Monitor admin actions, security alerts, and compliance</p>
          </div>
          <div className="flex gap-2">
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value as any)}
              className="border rounded px-3 py-2"
            >
              <option value="today">Today</option>
              <option value="week">Last 7 days</option>
              <option value="month">Last 30 days</option>
              <option value="all">All time</option>
            </select>
            <Button variant="outline" onClick={loadAllData}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      )}

      {/* Alert Banner */}
      {unacknowledgedAlerts.length > 0 && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            <p className="font-medium text-red-800">
              {unacknowledgedAlerts.length} unacknowledged security alert{unacknowledgedAlerts.length > 1 ? 's' : ''}
            </p>
            <Button 
              size="sm" 
              variant="outline" 
              className="ml-auto"
              onClick={() => setActiveTab('alerts')}
            >
              View Alerts
            </Button>
          </div>
        </div>
      )}

      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Audit Logs</p>
                <p className="text-2xl font-bold">{auditLogs.length}</p>
              </div>
              <FileText className="h-6 w-6 text-gray-300" />
            </div>
          </CardContent>
        </Card>
        
        <Card className={unacknowledgedAlerts.length > 0 ? 'border-red-300 bg-red-50' : ''}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Active Alerts</p>
                <p className="text-2xl font-bold text-red-600">{unacknowledgedAlerts.length}</p>
              </div>
              <AlertTriangle className="h-6 w-6 text-red-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Failed Logins</p>
                <p className="text-2xl font-bold">{loginAttempts.filter(l => !l.success).length}</p>
              </div>
              <Lock className="h-6 w-6 text-gray-300" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Export Requests</p>
                <p className="text-2xl font-bold">{exportRequests.filter(e => e.status === 'pending').length}</p>
              </div>
              <Download className="h-6 w-6 text-gray-300" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Deletion Requests</p>
                <p className="text-2xl font-bold">{deletionRequests.filter(d => d.status === 'pending').length}</p>
              </div>
              <Trash2 className="h-6 w-6 text-gray-300" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b overflow-x-auto">
        {[
          { id: 'audit', label: 'Admin Audit Log', icon: FileText },
          { id: 'activity', label: 'User Activity', icon: Activity },
          { id: 'alerts', label: 'Security Alerts', icon: AlertTriangle, badge: unacknowledgedAlerts.length },
          { id: 'compliance', label: 'Compliance', icon: Shield },
          { id: 'logins', label: 'Login Attempts', icon: Key }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as ActiveTab)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === tab.id
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
            {tab.badge && tab.badge > 0 && (
              <span className="ml-1 px-1.5 py-0.5 text-xs bg-red-500 text-white rounded-full">
                {tab.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Admin Audit Log Tab */}
      {activeTab === 'audit' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Admin Audit Log</CardTitle>
              <div className="flex gap-2">
                <Input
                  placeholder="Filter by action..."
                  value={actionFilter}
                  onChange={(e) => setActionFilter(e.target.value)}
                  className="w-48"
                />
                <Button variant="outline" onClick={handleExportLogs}>
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-[600px] overflow-y-auto">
              {filteredAuditLogs.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No audit logs found</p>
              ) : (
                filteredAuditLogs.map(log => (
                  <div 
                    key={log.id} 
                    className="p-3 border rounded-lg hover:bg-gray-50 cursor-pointer"
                    onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id!)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {expandedLog === log.id ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        <Badge className={getActionColor(log.action)}>
                          {log.action}
                        </Badge>
                        {log.targetId && (
                          <code className="text-xs bg-gray-100 px-2 py-0.5 rounded">
                            {log.targetId.substring(0, 12)}...
                          </code>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span>{log.adminId.substring(0, 8)}...</span>
                        <span>{formatDate(log.timestamp)}</span>
                      </div>
                    </div>
                    
                    {expandedLog === log.id && (
                      <div className="mt-3 pt-3 border-t">
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-gray-500">Admin ID</p>
                            <code className="text-xs">{log.adminId}</code>
                          </div>
                          <div>
                            <p className="text-gray-500">Target ID</p>
                            <code className="text-xs">{log.targetId || 'N/A'}</code>
                          </div>
                          {log.ipAddress && (
                            <div>
                              <p className="text-gray-500">IP Address</p>
                              <code className="text-xs">{log.ipAddress}</code>
                            </div>
                          )}
                          {log.userAgent && (
                            <div>
                              <p className="text-gray-500">User Agent</p>
                              <code className="text-xs line-clamp-1">{log.userAgent}</code>
                            </div>
                          )}
                        </div>
                        {log.details && (
                          <div className="mt-3">
                            <p className="text-gray-500 text-sm">Details</p>
                            <pre className="text-xs bg-gray-100 p-2 rounded mt-1 overflow-x-auto">
                              {JSON.stringify(log.details, null, 2)}
                            </pre>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* User Activity Tab */}
      {activeTab === 'activity' && (
        <Card>
          <CardHeader>
            <CardTitle>User Activity Logs</CardTitle>
            <CardDescription>Sensitive actions performed by users</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-[600px] overflow-y-auto">
              {activityLogs.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No activity logs found</p>
              ) : (
                activityLogs.map(log => (
                  <div key={log.id} className="p-3 border rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Badge variant="outline">{log.action}</Badge>
                        <code className="text-xs text-gray-500">{log.userId.substring(0, 12)}...</code>
                      </div>
                      <span className="text-sm text-gray-500">{formatDate(log.timestamp)}</span>
                    </div>
                    {log.details && (
                      <p className="text-sm text-gray-600 mt-2">{JSON.stringify(log.details)}</p>
                    )}
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Security Alerts Tab */}
      {activeTab === 'alerts' && (
        <Card>
          <CardHeader>
            <CardTitle>Security Alerts</CardTitle>
            <CardDescription>Anomalies and suspicious activity</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {alerts.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No security alerts</p>
              ) : (
                alerts.map(alert => (
                  <div 
                    key={alert.id} 
                    className={`p-4 border rounded-lg ${!alert.acknowledged ? 'border-red-200 bg-red-50' : ''}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Badge className={getAlertSeverityColor(alert.severity)}>
                            {alert.severity}
                          </Badge>
                          <span className="font-medium">{alert.type}</span>
                          {alert.acknowledged && (
                            <CheckCircle className="h-4 w-4 text-green-500" />
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mt-2">{alert.message}</p>
                        <p className="text-xs text-gray-400 mt-2">
                          {formatDate(alert.timestamp)}
                          {alert.userId && ` • User: ${alert.userId.substring(0, 8)}...`}
                        </p>
                      </div>
                      {!alert.acknowledged && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleAcknowledgeAlert(alert.id!)}
                        >
                          Acknowledge
                        </Button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Compliance Tab */}
      {activeTab === 'compliance' && (
        <div className="grid gap-6 md:grid-cols-2">
          {/* Data Export Requests */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Download className="h-5 w-5" />
                Data Export Requests (GDPR)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {exportRequests.length === 0 ? (
                  <p className="text-center text-gray-500 py-4">No export requests</p>
                ) : (
                  exportRequests.map(req => (
                    <div key={req.id} className="p-3 border rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <code className="text-xs">{req.userId}</code>
                          <p className="text-xs text-gray-500 mt-1">{formatDate(req.requestedAt)}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={
                            req.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                            req.status === 'completed' ? 'bg-green-100 text-green-700' :
                            'bg-gray-100'
                          }>
                            {req.status}
                          </Badge>
                          {req.status === 'pending' && (
                            <Button
                              size="sm"
                              onClick={() => handleProcessExport(req.id!)}
                            >
                              Process
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          {/* Deletion Requests */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trash2 className="h-5 w-5" />
                Account Deletion Requests
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {deletionRequests.length === 0 ? (
                  <p className="text-center text-gray-500 py-4">No deletion requests</p>
                ) : (
                  deletionRequests.map(req => (
                    <div key={req.id} className="p-3 border rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <code className="text-xs">{req.userId}</code>
                          <p className="text-xs text-gray-500 mt-1">
                            Reason: {req.reason || 'Not specified'}
                          </p>
                          <p className="text-xs text-gray-400">{formatDate(req.requestedAt)}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {req.status === 'pending' ? (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600"
                                onClick={() => handleProcessDeletion(req.id!, false)}
                              >
                                <XCircle className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => handleProcessDeletion(req.id!, true)}
                              >
                                <CheckCircle className="h-4 w-4" />
                              </Button>
                            </>
                          ) : (
                            <Badge className={
                              req.status === 'completed' ? 'bg-green-100 text-green-700' :
                              req.status === 'rejected' ? 'bg-red-100 text-red-700' :
                              'bg-gray-100'
                            }>
                              {req.status}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Login Attempts Tab */}
      {activeTab === 'logins' && (
        <Card>
          <CardHeader>
            <CardTitle>Login Attempts</CardTitle>
            <CardDescription>Recent authentication attempts</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-[600px] overflow-y-auto">
              {loginAttempts.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No login attempts recorded</p>
              ) : (
                loginAttempts.map(attempt => (
                  <div 
                    key={attempt.id} 
                    className={`p-3 border rounded-lg ${!attempt.success ? 'border-red-200 bg-red-50' : ''}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {attempt.success ? (
                          <CheckCircle className="h-5 w-5 text-green-500" />
                        ) : (
                          <XCircle className="h-5 w-5 text-red-500" />
                        )}
                        <div>
                          <code className="text-xs">{attempt.userId || attempt.email}</code>
                          <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                            <Globe className="h-3 w-3" />
                            <span>{attempt.ipAddress}</span>
                            {attempt.location && <span>• {attempt.location}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge variant="outline">{attempt.method}</Badge>
                        <p className="text-xs text-gray-400 mt-1">{formatDate(attempt.timestamp)}</p>
                      </div>
                    </div>
                    {!attempt.success && attempt.failureReason && (
                      <p className="text-xs text-red-600 mt-2">{attempt.failureReason}</p>
                    )}
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
